import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../../shared/store/store';
import { unitService } from '../services/unitService';
import { UnitState, UnitType } from '../types/unit.types';

const tok = (s: RootState) => s.auth.accessToken ?? '';

export const fetchUnits = createAsyncThunk(
    'sellerUnit/fetchAll',
    async (params: Record<string, any>, { getState }) => {
        const { data } = await unitService.getUnits(params, tok(getState() as RootState));
        return data;
    }
);

export const createUnit = createAsyncThunk(
    'sellerUnit/create',
    async (payload: { name: string; symbol: string; type: UnitType }, { getState }) => {
        const { data } = await unitService.createUnit(payload, tok(getState() as RootState));
        return data.unit;
    }
);

export const updateUnit = createAsyncThunk(
    'sellerUnit/update',
    async ({ id, body }: { id: string; body: Partial<{ name: string; symbol: string; status: string }> }, { getState }) => {
        const { data } = await unitService.updateUnit(id, body, tok(getState() as RootState));
        return data.unit;
    }
);

export const deleteUnit = createAsyncThunk(
    'sellerUnit/delete',
    async (id: string, { getState }) => {
        await unitService.deleteUnit(id, tok(getState() as RootState));
        return id;
    }
);

export const toggleUnitStatus = createAsyncThunk(
    'sellerUnit/toggleStatus',
    async ({ id, status }: { id: string; status: 'ACTIVE' | 'INACTIVE' }, { getState }) => {
        await unitService.toggleStatus(id, status, tok(getState() as RootState));
        return { id, status };
    }
);

const initialState: UnitState = {
    units  : [],
    total  : 0,
    page   : 1,
    loading: false,
    error  : null,
};

const unitSlice = createSlice({
    name: 'sellerUnit',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchUnits.pending,   (s) => { s.loading = true; s.error = null; })
            .addCase(fetchUnits.fulfilled, (s, a) => {
                s.loading = false;
                s.units = a.payload.units;
                s.total = a.payload.total;
                s.page  = a.payload.page;
            })
            .addCase(fetchUnits.rejected,  (s, a) => { s.loading = false; s.error = a.error.message ?? null; });

        builder
            .addCase(createUnit.pending,   (s) => { s.loading = true; s.error = null; })
            .addCase(createUnit.fulfilled, (s, a) => { s.loading = false; s.units.push(a.payload); s.total += 1; })
            .addCase(createUnit.rejected,  (s, a) => { s.loading = false; s.error = a.error.message ?? null; });

        builder
            .addCase(updateUnit.fulfilled, (s, a) => {
                const i = s.units.findIndex(u => u._id === a.payload._id);
                if (i !== -1) s.units[i] = a.payload;
            });

        builder
            .addCase(deleteUnit.fulfilled, (s, a) => {
                s.units = s.units.filter(u => u._id !== a.payload);
                s.total -= 1;
            });

        builder
            .addCase(toggleUnitStatus.fulfilled, (s, a) => {
                const u = s.units.find(u => u._id === a.payload.id);
                if (u) u.status = a.payload.status;
            });
    },
});

export default unitSlice.reducer;
